import { createElement } from 'react';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Entity, UserEntity } from '@backstage/catalog-model';

/** Generic helper type for render callbacks, parameterized by entity type */
type RenderProps<T extends Entity = Entity> = { entity: T };

/** Type guard to check if an entity is a UserEntity */
function isUserEntity(entity: Entity): entity is UserEntity {
  return entity.kind === 'User';
}

/** Safely extract display name from a UserEntity */
function getUserDisplayName(entity: Entity): string {
  if (isUserEntity(entity)) {
    return (
      entity.spec.profile?.displayName ||
      entity.metadata.title ||
      entity.metadata.name
    );
  }
  return entity.metadata.title || entity.metadata.name;
}

/**
 * Custom catalog table columns with adjusted widths to give more space to descriptions.
 * Shows different columns based on the entity kind being viewed.
 * Uses built-in column factories to maintain compatibility with Backstage updates.
 * @see https://backstage.io/docs/features/software-catalog/catalog-customization
 */
export const customCatalogColumns: CatalogTableColumnsFunc = ({ filters }) => {
  const defaultColumns = CatalogTable.columns;
  const kind = filters.kind?.value?.toLowerCase();

  // Custom name column that shows title or name without kind prefix
  const nameColumn = {
    ...defaultColumns.createNameColumn(),
    width: '15%',
    render: ({ entity }: RenderProps) =>
      createElement(EntityRefLink, {
        entityRef: entity,
        title: entity.metadata.title || entity.metadata.name,
      }),
  };

  // Domain & System: simpler view without type/lifecycle/system
  if (kind === 'domain' || kind === 'system') {
    return [
      { ...nameColumn, width: '20%' },
      { ...defaultColumns.createOwnerColumn(), width: '15%' },
      { ...defaultColumns.createMetadataDescriptionColumn(), width: '50%' },
      { ...defaultColumns.createTagsColumn(), width: '15%' },
    ];
  }

  if (kind === 'location') {
    return [
      { ...nameColumn, width: '30%' },
      { ...defaultColumns.createSpecTypeColumn(), width: '10%' },
      { ...defaultColumns.createSpecTargetsColumn(), width: '60%' },
    ];
  }

  // Group & User: organizational entities
  if (kind === 'group') {
    return [
      { ...nameColumn, width: '25%' },
      { ...defaultColumns.createMetadataDescriptionColumn(), width: '60%' },
      { ...defaultColumns.createTagsColumn(), width: '15%' },
    ];
  }

  // User: show displayName from profile
  if (kind === 'user') {
    const userNameColumn = {
      ...defaultColumns.createNameColumn(),
      width: '25%',
      render: ({ entity }: RenderProps) =>
        createElement(EntityRefLink, {
          entityRef: entity,
          title: getUserDisplayName(entity),
        }),
    };
    return [
      userNameColumn,
      { ...defaultColumns.createMetadataDescriptionColumn(), width: '60%' },
      { ...defaultColumns.createTagsColumn(), width: '15%' },
    ];
  }

  // Default: Component, API, Resource, etc.
  return [
    nameColumn,
    { ...defaultColumns.createSystemColumn(), width: '10%' },
    { ...defaultColumns.createOwnerColumn(), width: '10%' },
    { ...defaultColumns.createSpecTypeColumn(), width: '10%' },
    { ...defaultColumns.createSpecLifecycleColumn(), width: '10%' },
    { ...defaultColumns.createMetadataDescriptionColumn(), width: '35%' },
    { ...defaultColumns.createTagsColumn(), width: '10%' },
  ];
};
